import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { v4 as uuidv4 } from 'uuid'
import { Option } from '../../../components/Forms/FormSelect'
import { BookingPreference, WaypointType } from '../../../types/common'
import { CUSTOM_TEMPLATE_ID } from './PackagingItem'
import { IShipmentFormInput } from './schema'
import { LoadDto, ShipmentDto, TransitDetailDto, WaypointDto } from './types'

dayjs.extend(utc)

export const getCustomerName = (customers: any[], customerId?: number) => {
  return customers.find((customer) => customer.id === customerId)?.name
}

const mapBatchDetailsWithInternalId = (batchDetails: any[] | undefined) => {
  if (!batchDetails) return []
  return batchDetails.map((batch) => ({
    ...batch,
    internalId: batch.internalId || uuidv4(),
  }))
}

export const getCarrierOption = (carrierId?: number, transitIdentifier?: string) => {
  if (!carrierId) {
    return 'noPreference'
  }
  if (transitIdentifier) {
    return 'preBookedTransit'
  }
  return 'preferredCarrier'
}

export const getFulfilmentRequired = (
  modeOfTransit?: number,
  carrierOption?: string,
  fulfilmentRequired?: boolean
): boolean => {
  const ROAD_TRANSPORT_MODE_ID = 3

  if (modeOfTransit === ROAD_TRANSPORT_MODE_ID && fulfilmentRequired === false) {
    return false
  }

  if (carrierOption === 'preBookedTransit') {
    return false
  }

  return true
}

export const getAvailableLocations = (
  allLocations: Option[],
  usedlocationIds: number[]
): Option[] => {
  return allLocations.filter((location) => !usedlocationIds.includes(+location.value))
}

export const mapToShipmentFormInput = (shipmentData: ShipmentDto): IShipmentFormInput => {
  if (
    shipmentData.tenantId === undefined ||
    shipmentData.customerId === undefined ||
    !shipmentData.bookings
  ) {
    throw new Error('Required shipment data is missing.')
  }

  const mapTransitDetail = (transitDetail: TransitDetailDto) => {
    return {
      id: transitDetail.id ?? undefined,
      carrierId: transitDetail.carrierId,
      carrier: transitDetail.carrier ?? undefined,
      modeOfTransit: transitDetail.transportModeId === 0 ? -1 : transitDetail.transportModeId ?? -1,
      transportMode: transitDetail.transportMode ?? undefined,
      transitIdentifier: transitDetail.transitIdentifier ?? undefined,
      crossBorder: transitDetail.isCrossBorder ?? undefined,
      carrierOption: getCarrierOption(transitDetail.carrierId, transitDetail.transitIdentifier),
      fulfilmentRequired: transitDetail.fulfilmentRequired ?? undefined,
      scheduledDate: transitDetail.scheduledDate
        ? new Date(transitDetail.scheduledDate + 'Z')
        : undefined,
    }
  }

  const getLocationIdFromWaypoint = (waypoint: WaypointDto) => {
    return waypoint.locationId ?? -1
  }

  return {
    id: shipmentData.id ?? undefined,
    submittedDate: shipmentData.submittedDate ? new Date(shipmentData.submittedDate) : undefined,
    tenantId: shipmentData.tenantId ?? undefined,
    revision: shipmentData.revision ?? undefined,
    validFrom: shipmentData.validFrom ?? undefined,
    validTo: shipmentData.validTo ?? undefined,
    customerId: shipmentData.customerId,
    customerName: shipmentData.customer?.name ?? undefined,
    customerReferenceNumber: shipmentData.customerReferenceNumber,
    shipmentNumber: shipmentData.code ?? undefined,
    description: shipmentData.description ?? '',
    statuses: shipmentData.statuses ?? [],
    status: shipmentData.status ?? '',
    bookings: shipmentData.bookings.map((consignment) => {
      if (!consignment.waypoints) {
        throw new Error('Consignment waypoints are missing.')
      }

      const shipFromWaypoint = consignment.waypoints.find(
        (waypoint) => waypoint.waypointTypeId === 1
      )
      const deliverToWaypoint =
        consignment.waypoints.find((waypoint) => waypoint.waypointTypeId === 3) || shipFromWaypoint

      if (!shipFromWaypoint || !deliverToWaypoint) {
        throw new Error('ShipFrom or DeliverTo waypoint is missing.')
      }

      const intermediateWaypoints = consignment.waypoints.filter(
        (waypoint) => waypoint.waypointTypeId === 2
      )

      const returnToWaypoint = consignment.waypoints.find(
        (waypoint) => waypoint.waypointTypeId === 0
      )

      const emptyReturnCollection =
        consignment.containerCollections && consignment.containerCollections.length > 0

      const shipFrom = {
        id: shipFromWaypoint.id ?? undefined,
        locationId: getLocationIdFromWaypoint(shipFromWaypoint),
        consignmentId: consignment.id ?? undefined,
        location: shipFromWaypoint.location ?? undefined,
        locationRevision: shipFromWaypoint.locationRevision ?? undefined,
        contactId: shipFromWaypoint.contactId ?? undefined,
        clearingRequired: shipFromWaypoint.clearingRequired ?? undefined,
        specialInstructions: shipFromWaypoint.instructions ?? undefined,
        loadingDate: shipFromWaypoint.requestedDate
          ? new Date(shipFromWaypoint.requestedDate + 'Z')
          : null,
        emptyContainerCollection: emptyReturnCollection ? true : false,
        returnTo: !!returnToWaypoint,
        services: shipFromWaypoint.services ?? [],
        transitDetails: mapTransitDetail(
          shipFromWaypoint.transitDetail ?? {
            transportModeId: -1,
          }
        ),
        defaultValue: undefined,
      }

      const deliverTo = {
        id: deliverToWaypoint.id ?? undefined,
        consignmentId: consignment.id ?? undefined,
        locationId: getLocationIdFromWaypoint(deliverToWaypoint),
        location: deliverToWaypoint.location ?? undefined,
        locationRevision: deliverToWaypoint.locationRevision ?? undefined,
        clearingRequired: deliverToWaypoint.clearingRequired ?? undefined,
        contactId: deliverToWaypoint.contactId ?? undefined,
        deliveryDate: deliverToWaypoint.requestedDate
          ? new Date(deliverToWaypoint.requestedDate + 'Z')
          : undefined,
        specialInstructions: deliverToWaypoint.instructions ?? undefined,
        services: deliverToWaypoint.services ?? [],
      }

      const waypoints = intermediateWaypoints.map((waypoint) => ({
        id: waypoint.id ?? undefined,
        locationId: getLocationIdFromWaypoint(waypoint),
        clearingRequired: waypoint.clearingRequired ?? undefined,
        location: waypoint.location ?? undefined,
        locationRevision: waypoint.locationRevision ?? undefined,
        loadingDate: waypoint.requestedDate ? new Date(waypoint.requestedDate + 'Z') : undefined,
        specialInstructions: waypoint.instructions ?? undefined,
        consignmentId: consignment.id ?? undefined,
        contactId: waypoint.contactId ?? undefined,
        services: waypoint.services ?? [],
        transitDetails: mapTransitDetail(
          waypoint.transitDetail ?? {
            transportModeId: -1,
          }
        ),
      }))

      const packaging = consignment.loads?.map((load: LoadDto) => {
        if (!load.detail || !load.detail.physicalProperties) {
          throw new Error('Load detail or physical properties are missing.')
        }

        const props = load.detail.physicalProperties
        const templateProps = (load.detail as any).loadTemplate?.physicalProperties

        const pkg: any = {
          id: load.id ?? undefined,

          isHazardous: load.isHazardous ?? false,
          shipmentBookingId: load.shipmentBookingId ?? undefined,
          packagingTemplateId: load.detail.loadTemplateId ?? undefined,
          physicalPropertiesId: props.id ?? undefined,
          сonsignmentId: consignment.id ?? undefined,
          packagingDetailId: load.detail.id ?? undefined,
          numberOfPackages: load.quantity ?? undefined,
          useDeclaredValue: undefined,
          declaredContentDescription: load.description,
          isStackable: load.detail.isStackable ?? undefined,
          declaredValue: load.value ?? undefined,
          physicalProperties: {
            grossWeight: props.grossWeight ?? undefined,
            netWeight: props.netWeight ?? undefined,
            tareWeight: (props as any).tareWeight ?? templateProps?.netWeight ?? undefined,
            length: props.length ?? undefined,
            width: props.width ?? undefined,
            height: props.height ?? undefined,
            weightMeasureUnitId:
              props.weightMeasureUnitId !== null && props.weightMeasureUnitId !== undefined
                ? props.weightMeasureUnitId
                : null,
            volumeMeasureUnitId:
              props.volumeMeasureUnitId !== null && props.volumeMeasureUnitId !== undefined
                ? props.volumeMeasureUnitId
                : null,
            densityMeasureUnitId:
              props.densityMeasureUnitId !== null && props.densityMeasureUnitId !== undefined
                ? props.densityMeasureUnitId
                : null,
            lengthMeasureUnitId:
              props.lengthMeasureUnitId !== null && props.lengthMeasureUnitId !== undefined
                ? props.lengthMeasureUnitId
                : null,
            isRectangular: props.width !== undefined ? true : false,
            useDimensions:
              props.length !== undefined || props.width !== undefined || props.height !== undefined
                ? true
                : false,
          },
          products: load.contents?.map((content) => {
            const productPP = content.physicalProperties ?? {}
            const useDimensions = Boolean(
              (productPP as any).length || (productPP as any).width || (productPP as any).height
            )

            return {
              id: content.id ?? undefined,

              productDescription: content.description ?? undefined,
              productId: content.productId ?? undefined,
              countryOfOriginId: content.countryOfOriginId ?? undefined,
              quantity: content.quantity ?? 0,
              unitPrice: content.unitPrice ?? 0,
              useProduct: content.productId !== undefined ? true : undefined,
              defaultValue: undefined,
              physicalPropertiesId: content.physicalPropertiesId ?? undefined,
              ...(productPP &&
              ((productPP as any)?.weightMeasureUnitId ||
                (productPP as any)?.volumeMeasureUnitId ||
                (productPP as any)?.densityMeasureUnitId ||
                (productPP as any)?.lengthMeasureUnitId)
                ? {
                    physicalProperties: {
                      ...productPP,
                      weightMeasureUnitId: (productPP as any)?.weightMeasureUnitId ?? null,
                      volumeMeasureUnitId: (productPP as any)?.volumeMeasureUnitId ?? null,
                      densityMeasureUnitId: (productPP as any)?.densityMeasureUnitId ?? null,
                      lengthMeasureUnitId: (productPP as any)?.lengthMeasureUnitId ?? null,
                      isRectangular: (productPP as any)?.width ? true : false,
                      useDimensions,
                    },
                  }
                : {}),
              hsHeadingId: content.hsHeadingId ?? undefined,
              hsSubheadingId: content.hsSubheadingId ?? undefined,
              temperatureSettingId: content.temperatureSettingId ?? undefined,
              temperatureSetting: content.temperatureSetting ?? undefined,
              temperatureControlled: content.temperatureSetting ? true : false,
              batchDetails: mapBatchDetailsWithInternalId(content.batchDetails),
            }
          }),
        }

        if (!load.contents || load.contents.length === 0) {
          pkg.useDeclaredValue = true
          if (load.value !== undefined) {
            pkg.amountInsured = load.value
            pkg.insuranceRequired = true
          }
        } else {
          pkg.useDeclaredValue = false
        }

        if (load.temperatureSetting) {
          pkg.temperatureControlled = true
          pkg.temperatureSettingId = load.temperatureSetting.id ?? undefined
          pkg.temperatureRange = load.temperatureSetting.temperatureRangeId ?? undefined
          pkg.setPointUnitId = load.temperatureSetting.temperatureUnitId ?? undefined
          pkg.setPoint = load.temperatureSetting.setPoint ?? undefined
          pkg.lowerWarning = load.temperatureSetting.lowerThresholdWarning ?? undefined
          pkg.upperWarning = load.temperatureSetting.upperThresholdWarning ?? undefined
          pkg.lowerCritical = load.temperatureSetting.lowerThresholdCritical ?? undefined
          pkg.upperCritical = load.temperatureSetting.upperThresholdCritical ?? undefined
        }

        return pkg
      })
      return {
        id: consignment.id ?? undefined,
        bookingId: consignment.bookingId ?? undefined,
        referenceNumber:
          consignment.referenceNumber === '' ? undefined : consignment.referenceNumber,
        trackingNumber: consignment.trackingNumber ?? undefined,
        currencyId: consignment.currencyId!,
        currency: consignment.currency ?? undefined,
        containerCollections:
          consignment.containerCollections?.map((x) => {
            let carrierOption = BookingPreference.NoPreferredLine
            if (x.carrierId) {
              carrierOption = BookingPreference.PreferredLine
            }
            if (x.croValidFrom && x.croValidTo) {
              carrierOption = BookingPreference.PreBooked
            }
            return {
              id: x.id,
              shipmentBookingId: consignment.id ?? 0,
              loadTemplateId: x.loadTemplateId ?? -1,
              quantity: x.quantity ?? 0,
              carrierId: x.carrierId,
              locationId: x.locationId,
              croNumber: x.croNumber,
              croValidFrom: x.croValidFrom
                ? new Date(x.croValidFrom)
                : x.croValidFrom === null
                ? undefined
                : x.croValidFrom,
              croValidTo: x.croValidTo
                ? new Date(x.croValidTo)
                : x.croValidTo === null
                ? undefined
                : x.croValidTo,
              carrierOption: carrierOption,
            }
          }) ?? [],
        description: consignment.description ?? '',
        shipperId: consignment.shipperId ?? -1,
        shipperRevision: consignment.shipperRevision ?? undefined,
        shipper: consignment.shipper ?? undefined,
        loadTypeId: consignment.loadTypeId ?? -1,
        loadType: consignment.loadType ?? undefined,
        shipFrom,
        deliverTo,
        returnTo: returnToWaypoint
          ? {
              id: returnToWaypoint.id ?? undefined,
              locationId: getLocationIdFromWaypoint(returnToWaypoint),
              location: returnToWaypoint.location ?? undefined,
              locationRevision: returnToWaypoint.locationRevision ?? undefined,
              contactId: returnToWaypoint.contactId ?? undefined,
              specialInstructions: returnToWaypoint.instructions ?? undefined,
              consignmentId: consignment.id ?? undefined,
            }
          : undefined,
        packaging,
        waypoints,
      }
    }),
  }
}

export const mapToShipmentData = (data: IShipmentFormInput): ShipmentDto => {
  const shipmentData: ShipmentDto = {
    id: data.id ?? undefined,

    tenantId: data.tenantId ?? undefined,
    customerId: data.customerId,
    validFrom:
      typeof data.validFrom === 'string'
        ? data.validFrom
        : data.validFrom instanceof Date
        ? data.validFrom.toISOString()
        : undefined,
    validTo:
      typeof data.validTo === 'string'
        ? data.validTo
        : data.validTo instanceof Date
        ? data.validTo.toISOString()
        : undefined,
    revision: data.revision ?? undefined,
    customerReferenceNumber:
      data.customerReferenceNumber === '' || data.customerReferenceNumber === null
        ? undefined
        : data.customerReferenceNumber,
    code: data.shipmentNumber ?? undefined,
    description: data.description ?? undefined,
    submittedDate: undefined,
    acceptedDate: undefined,
    underReviewUntil: undefined,
    underReviewByUserId: undefined,
    customer: undefined,
    shipper: undefined,
    bookings:
      data.bookings?.map((consignment) => {
        const baseSequence = 1

        const waypoints: WaypointDto[] = [
          {
            id: consignment.shipFrom.id,
            consignmentId: consignment.id,
            bookingId: consignment.bookingId,
            waypointTypeId: WaypointType.shipFrom,
            contactId: consignment.shipFrom.contactId ?? undefined,
            locationId: consignment.shipFrom.locationId ?? -1,
            locationRevision: consignment.shipFrom.locationRevision ?? undefined,
            requestedDate: consignment.shipFrom.loadingDate
              ? dayjs(consignment.shipFrom.loadingDate).utc().format('YYYY-MM-DDTHH:mm:ss')
              : undefined,
            sequence: baseSequence,
            clearingRequired: consignment.shipFrom.clearingRequired ?? false,
            instructions: consignment.shipFrom.specialInstructions ?? undefined,
            services: consignment.shipFrom.services ?? [],
            transitDetail: {
              id: consignment.shipFrom.transitDetails.id ?? 0,
              waypointId: consignment.shipFrom.id ?? 0,
              transportModeId:
                consignment.shipFrom.transitDetails.modeOfTransit === -1
                  ? 0
                  : consignment.shipFrom.transitDetails.modeOfTransit ?? 0,
              isCrossBorder: consignment.shipFrom.transitDetails.crossBorder ?? undefined,
              carrierId: consignment.shipFrom.transitDetails?.carrierId ?? undefined,
              transitIdentifier: consignment.shipFrom.transitDetails.transitIdentifier ?? undefined,
              fulfilmentRequired: getFulfilmentRequired(
                consignment.shipFrom.transitDetails.modeOfTransit,
                consignment.shipFrom.transitDetails.carrierOption,
                consignment.shipFrom.transitDetails.fulfilmentRequired
              ),
              scheduledDate: consignment.shipFrom.transitDetails.scheduledDate
                ? dayjs(consignment.shipFrom.transitDetails.scheduledDate)
                    .utc()
                    .format('YYYY-MM-DDTHH:mm:ss')
                : undefined,
            },
          },
          ...(consignment.waypoints?.map((waypoint, idx) => ({
            id: waypoint.id,
            consignmentId: consignment.id ?? 0,
            bookingId: consignment.bookingId,
            waypointTypeId: WaypointType.waypoint,
            contactId: waypoint.contactId ?? undefined,
            locationId: waypoint.locationId ?? -1,
            locationRevision: waypoint.locationRevision ?? undefined,
            scheduledDate: waypoint.loadingDate
              ? dayjs(waypoint.loadingDate).utc().format('YYYY-MM-DDTHH:mm:ss')
              : undefined,
            sequence: idx + baseSequence + 1,
            clearingRequired: waypoint.clearingRequired ?? false,
            instructions: waypoint.specialInstructions ?? undefined,
            services: waypoint.services ?? [],
            transitDetail: {
              id: waypoint.transitDetails.id ?? 0,
              waypointId: waypoint.id ?? 0,
              transportModeId:
                waypoint.transitDetails.modeOfTransit === -1
                  ? 0
                  : waypoint.transitDetails.modeOfTransit ?? 0,
              isCrossBorder: waypoint.transitDetails.crossBorder ?? undefined,
              carrierId: waypoint.transitDetails.carrierId,
              transitIdentifier: waypoint.transitDetails.transitIdentifier ?? undefined,
              fulfilmentRequired: getFulfilmentRequired(
                waypoint.transitDetails.modeOfTransit,
                waypoint.transitDetails.carrierOption,
                waypoint.transitDetails.fulfilmentRequired
              ),
              scheduledDate: waypoint.transitDetails.scheduledDate
                ? dayjs(waypoint.transitDetails.scheduledDate).utc().format('YYYY-MM-DDTHH:mm:ss')
                : undefined,
            },
          })) ?? []),
          {
            id: consignment.deliverTo.id,
            consignmentId: consignment.id,
            bookingId: consignment.bookingId,
            waypointTypeId: WaypointType.deliverTo,
            contactId: consignment.deliverTo.contactId ?? undefined,
            locationId: consignment.deliverTo.locationId ?? -1,
            locationRevision: consignment.deliverTo.locationRevision ?? undefined,
            requestedDate: consignment.deliverTo.deliveryDate
              ? dayjs(consignment.deliverTo.deliveryDate).utc().format('YYYY-MM-DDTHH:mm:ss')
              : undefined,
            sequence: consignment.waypoints?.length
              ? consignment.waypoints.length + 1 + baseSequence
              : 2,
            clearingRequired: consignment.deliverTo.clearingRequired ?? false,
            instructions: consignment.deliverTo.specialInstructions ?? undefined,
            services: consignment.deliverTo.services ?? [],
            transitDetail: consignment.deliverTo.transitDetails
              ? {
                  id: consignment.deliverTo.transitDetails.id ?? 0,
                  waypointId: consignment.deliverTo.id ?? 0,
                  transportModeId:
                    consignment.deliverTo.transitDetails.modeOfTransit === -1
                      ? 0
                      : consignment.deliverTo.transitDetails.modeOfTransit ?? 0,
                  isCrossBorder: consignment.deliverTo.transitDetails.crossBorder ?? undefined,
                  carrierId: consignment.deliverTo.transitDetails.carrierId ?? undefined,
                  transitIdentifier:
                    consignment.deliverTo.transitDetails.transitIdentifier ?? undefined,
                  fulfilmentRequired: getFulfilmentRequired(
                    consignment.deliverTo.transitDetails.modeOfTransit,
                    consignment.deliverTo.transitDetails.carrierOption,
                    consignment.deliverTo.transitDetails.fulfilmentRequired
                  ),
                  scheduledDate: consignment.deliverTo.transitDetails.scheduledDate
                    ? dayjs(consignment.deliverTo.transitDetails.scheduledDate)
                        .utc()
                        .format('YYYY-MM-DDTHH:mm:ss')
                    : undefined,
                }
              : undefined,
          },
        ]

        if (consignment.shipFrom.returnTo && consignment.returnTo?.locationId) {
          waypoints.push({
            id: consignment.returnTo.id ?? 0,
            bookingId: consignment.bookingId,
            consignmentId: consignment.id ?? 0,
            waypointTypeId: WaypointType.return,
            contactId: consignment.returnTo.contactId ?? undefined,
            locationId: consignment.returnTo.locationId ?? -1,
            locationRevision: consignment.returnTo.locationRevision ?? undefined,
            sequence: -1,
            clearingRequired: false,
            instructions: consignment.returnTo.specialInstructions ?? undefined,
          })
        }

        const loads = consignment.packaging?.map((packaging) => {
          const loadTemplateId =
            packaging.packagingTemplateId === CUSTOM_TEMPLATE_ID
              ? undefined
              : packaging.packagingTemplateId || undefined

          const detail = {
            id: packaging.packagingDetailId ?? 0,
            loadTemplateId: loadTemplateId,
            loadTemplate: undefined,
            physicalPropertiesId: packaging.physicalPropertiesId ?? 0,
            physicalProperties: {
              id: packaging.physicalPropertiesId ?? 0,
              weightMeasureUnitId:
                packaging.physicalProperties?.weightMeasureUnitId !== null &&
                packaging.physicalProperties?.weightMeasureUnitId !== undefined
                  ? packaging.physicalProperties?.weightMeasureUnitId
                  : null,
              volumeMeasureUnitId:
                packaging.physicalProperties?.volumeMeasureUnitId !== null &&
                packaging.physicalProperties?.volumeMeasureUnitId !== undefined
                  ? packaging.physicalProperties?.volumeMeasureUnitId
                  : null,
              densityMeasureUnitId:
                packaging.physicalProperties?.densityMeasureUnitId !== null &&
                packaging.physicalProperties?.densityMeasureUnitId !== undefined
                  ? packaging.physicalProperties?.densityMeasureUnitId
                  : null,
              lengthMeasureUnitId:
                packaging.physicalProperties?.lengthMeasureUnitId !== null &&
                packaging.physicalProperties?.lengthMeasureUnitId !== undefined
                  ? packaging.physicalProperties?.lengthMeasureUnitId
                  : null,
              netWeight: packaging.physicalProperties?.netWeight ?? undefined,
              grossWeight: packaging.physicalProperties?.grossWeight ?? undefined,
              chargeableWeight: undefined,
              volume: undefined,
              density: undefined,
              height: packaging.physicalProperties?.height ?? undefined,
              length: packaging.physicalProperties?.length ?? undefined,
              width: packaging.physicalProperties?.width ?? undefined,
              radius: undefined,
              isRectangular: packaging.physicalProperties?.width !== undefined ? true : false,
              useDimensions:
                packaging.physicalProperties?.length !== undefined ||
                packaging.physicalProperties?.width !== undefined ||
                packaging.physicalProperties?.height !== undefined
                  ? true
                  : false,
            },
            quantity: packaging.numberOfPackages ?? 1,
            containerNumber: undefined,
            genSetRequired: undefined,
            isStackable: packaging.isStackable ?? undefined,
          }

          let temperatureSetting = undefined
          if (packaging.temperatureControlled) {
            temperatureSetting = {
              id: packaging.temperatureSettingId ?? 0,
              temperatureRangeId: packaging.temperatureRange ?? 0,
              temperatureRange: packaging.temperatureRange
                ? { id: packaging.temperatureRange }
                : undefined,
              temperatureUnitId: packaging.setPointUnitId ?? 0,
              temperatureUnit: packaging.setPointUnitId
                ? {
                    id: packaging.setPointUnitId,
                    measureTypeId: 0,
                    siConversionFactor: 1,
                    isSiUnit: false,
                  }
                : undefined,
              setPoint: packaging.setPoint ?? 0,
              upperThresholdWarning: packaging.upperWarning ?? undefined,
              upperThresholdCritical: packaging.upperCritical ?? undefined,
              lowerThresholdWarning: packaging.lowerWarning ?? undefined,
              lowerThresholdCritical: packaging.lowerCritical ?? undefined,
            }
          }

          const loadContents = !packaging.useDeclaredValue
            ? packaging.products?.map((product) => {
                const productPP = product.physicalProperties ?? {}
                const useDimensions = Boolean(
                  (productPP as any).length || (productPP as any).width || (productPP as any).height
                )
                return {
                  id: product.id ?? 0,
                  loadId: packaging.id ?? 0,
                  productId: product.useProduct ? product.productId ?? undefined : undefined,
                  productRevision: product.productRevision ?? undefined,
                  countryOfOriginId: product.countryOfOriginId ?? undefined,
                  unitPrice: product.unitPrice,
                  quantity: product.quantity,
                  description: product.productDescription ?? product.productName,
                  physicalPropertiesId: product.id
                    ? (product.physicalPropertiesId ?? undefined)
                    : undefined,
                  ...(productPP &&
                  ((productPP as any)?.weightMeasureUnitId ||
                    (productPP as any)?.volumeMeasureUnitId ||
                    (productPP as any)?.densityMeasureUnitId ||
                    (productPP as any)?.lengthMeasureUnitId)
                    ? {
                        physicalProperties: {
                          ...productPP,
                          weightMeasureUnitId: (productPP as any)?.weightMeasureUnitId ?? null,
                          volumeMeasureUnitId: (productPP as any)?.volumeMeasureUnitId ?? null,
                          densityMeasureUnitId: (productPP as any)?.densityMeasureUnitId ?? null,
                          lengthMeasureUnitId: (productPP as any)?.lengthMeasureUnitId ?? null,
                          isRectangular: (productPP as any)?.width ? true : false,
                          useDimensions,
                        },
                      }
                    : {}),
                  temperatureSettingId: product.id
                    ? (product.temperatureSettingId ?? undefined)
                    : undefined,
                  temperatureSetting: product.temperatureSetting
                    ? {
                        id: product.temperatureSetting.id ?? undefined,
                        temperatureRangeId:
                          product.temperatureSetting.temperatureRangeId ?? undefined,
                        temperatureUnitId:
                          product.temperatureSetting.temperatureUnitId ?? undefined,
                        setPoint: product.temperatureSetting.setPoint ?? undefined,
                        upperThresholdWarning:
                          product.temperatureSetting.upperThresholdWarning ?? undefined,
                        upperThresholdCritical:
                          product.temperatureSetting.upperThresholdCritical ?? undefined,
                        lowerThresholdWarning:
                          product.temperatureSetting.lowerThresholdWarning ?? undefined,
                        lowerThresholdCritical:
                          product.temperatureSetting.lowerThresholdCritical ?? undefined,
                      }
                    : undefined,
                  hsHeadingId: product.hsHeadingId ?? undefined,
                  hsSubheadingId: product.hsSubheadingId ?? undefined,
                  batchDetails: mapBatchDetailsWithInternalId(product.batchDetails),
                }
              })
            : undefined
          return {
            id: packaging.id ?? 0,
            consignmentId: consignment.id ?? 0,
            loadDetailId: packaging.packagingDetailId ?? 0,
            shipmentBookingId: consignment.id ?? 0,
            detail: detail,
            temperatureSettingId: temperatureSetting ? temperatureSetting.id ?? 0 : undefined,
            temperatureSetting: temperatureSetting,
            value: packaging.useDeclaredValue
              ? packaging.declaredValue
                ? packaging.declaredValue
                : undefined
              : undefined,
            quantity: packaging.numberOfPackages ?? 1,
            isHazardous: packaging.isHazardous ?? false,
            trackingNumbers: undefined,
            contents: loadContents,
            description: packaging.declaredContentDescription ?? undefined,
          }
        })

        return {
          id: consignment.id ?? 0,
          bookingId: consignment.bookingId ?? 0,
          containerCollections:
            consignment.containerCollections?.map((c) => ({
              id: c.id ? c.id : undefined,
              shipmentBookingId: consignment.id ?? 0,
              loadTemplateId: c.loadTemplateId,
              quantity: c.quantity,
              carrierId: c.carrierId,
              locationId: c.locationId,
              croNumber: c.croNumber,
              croValidFrom: c.croValidFrom
                ? new Date(c.croValidFrom)
                : c.croValidFrom === null
                ? undefined
                : c.croValidFrom,
              croValidTo: c.croValidTo
                ? new Date(c.croValidTo)
                : c.croValidTo === null
                ? undefined
                : c.croValidTo,
            })) ?? [],
          orderId: data.id ?? 0,
          shipperId: consignment.shipperId ?? undefined,
          shipperRevision: consignment.shipperRevision ?? undefined,
          shipper: consignment.shipper ?? undefined,
          referenceNumber: consignment.referenceNumber ?? '',
          trackingNumber: consignment.trackingNumber ?? undefined,
          currencyId: consignment.currencyId!,
          description: consignment.description ?? undefined,
          loads: loads ?? undefined,
          waypoints: waypoints,
          loadTypeId: consignment.loadTypeId ?? undefined,
          assetRequestAllocations: undefined,
          routeConsignmentHistories: undefined,
          shipment: undefined,
        }
      }) ?? undefined,
    assetRequests: undefined,
  }

  return shipmentData
}

export const getLocationAddress = (locations: any[], locationId: number) => {
  return locations.find((location) => location.id === locationId)?.address?.address1 ?? 'N/A'
}

export function mapContactsToOptions(contacts: any[] = []) {
  return contacts.map((contact) => ({
    label: `${contact?.contactType?.name} - ${contact?.name}`,
    value: contact.id,
  }))
}
export function mapCurrenciesToOptions(currencies: any[] = []) {
  return currencies.map((currency) => ({
    label: `${currency.ISO3} - ${currency.localisedSymbol}`,
    value: currency.id,
  }))
}

// Service mapping functions
export const mapServiceForDisplay = (service: any, services: any[]) => {
  const serviceDetails = services.find((s) => s.id.toString() === service.serviceId.toString())
  if (!serviceDetails) return null

  return {
    id: service.id,
    waypointId: service.waypointId,
    serviceId: service.serviceId,
    serviceName: serviceDetails.name,
    serviceDescription: serviceDetails.description,
    serviceCode: serviceDetails.code,
    loads: Array.isArray(service.loads) ? [...service.loads] : [],
  }
}

export const mapServicesToWaypoint = (services: any[], waypointId: number) => {
  return services.map((service) => ({
    ...service,
    waypointId,
  }))
}

export const getLocationRevision = (locations: any[], locationId: number) => {
  return locations.find((location) => location.id === locationId)?.revision ?? 0
}

export function getSelectedServices(
  consignment: any,
  waypointType: 'shipFrom' | 'deliverTo' | number,
  servicesList: any[],
  formatMessage: any,
  messages: any
) {
  let waypointLabel = ''
  let waypointServices: any[] = []

  if (waypointType === 'shipFrom') {
    waypointLabel = formatMessage(messages.shipFrom)
    waypointServices = consignment.shipFrom?.services || []
  } else if (waypointType === 'deliverTo') {
    waypointLabel = formatMessage(messages.deliverTo)
    waypointServices = consignment.deliverTo?.services || []
  } else if (typeof waypointType === 'number') {
    waypointLabel = `${formatMessage(messages.waypoint)} ${waypointType + 1}`
    waypointServices = consignment.waypoints?.[waypointType]?.services || []
  }

  const detailedServices = waypointServices.map((service: any) => {
    const serviceObj = servicesList.find((s: any) => s.id === service.serviceId)
    return {
      serviceName: serviceObj?.name || 'N/A',
      serviceDescription: serviceObj?.description || 'N/A',
      serviceCode: serviceObj?.code || 'N/A',
    }
  })

  return { waypointLabel, services: detailedServices }
}
